import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { trackPageVisit } from "../utils/analytics";
import Footer from "./Footer";
import SocialHandles from "./SocialHandles";

const Downloads: React.FC = () => {
  const { t } = useTranslation();
  const mobileScreenshots = [
    "/assets/mobile-app-ss/Screenshot_20260522-102017.png",
    "/assets/mobile-app-ss/Screenshot_20260522-102029.png",
    "/assets/mobile-app-ss/Screenshot_20260522-102131.png",
    "/assets/mobile-app-ss/Screenshot_20260522-102215.png",
    "/assets/mobile-app-ss/Screenshot_20260522-102236.png",
  ];

  useEffect(() => {
    trackPageVisit("downloads");
  }, []);

  return (
    <>
      <MetaHead
        title={t("downloads.title")}
        description={t("downloads.description") as string}
        keywords={t("downloads.keywords") as string}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 animate-fade-in">
        {/* Navbar */}
        <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                  Streak Spark
                </span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link
                  to="/"
                  className="text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-300"
                >
                  {t("downloads.home")}
                </Link>
                <Link
                  to="/app"
                  className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  {t("downloads.openApp")}
                </Link>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          {/* Hero Section */}
          <div className="mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary-600 via-accent-600 to-secondary-600 bg-clip-text text-transparent mb-4 leading-tight">
              {t("downloads.downloadsTitle")}
            </h1>

            <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-300 mb-6 max-w-3xl mx-auto leading-relaxed">
              {t("downloads.downloadsDescription")}
            </p>
          </div>

          {/* Screenshots Section */}
          <div className="glass-card p-8 rounded-2xl mb-8">
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
              {t("downloads.screenshots")}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {mobileScreenshots.map((screenshot, index) => (
                <img
                  key={screenshot}
                  src={screenshot}
                  alt={`${t("downloads.androidApp")} screenshot ${index + 1}`}
                  className="rounded-lg shadow-lg w-full h-full object-cover"
                  loading="lazy"
                />
              ))}
            </div>
          </div>

          {/* Content Section */}
          <div className="glass-card p-8 rounded-2xl mb-8">
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
              {t("downloads.getTheApp")}
            </h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
              {t("downloads.appDescription")}
            </p>

            <div className="bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 rounded-xl p-6 mb-6">
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.523 15.3414c-.5511 0-.9993-.4482-.9993-.9993s.4482-.9993.9993-.9993.9993.4482.9993.9993-.4482.9993-.9993.9993zm-11.046 0c-.5511 0-.9993-.4482-.9993-.9993s.4482-.9993.9993-.9993.9993.4482.9993.9993-.4482.9993-.9993.9993zM21.605 6.014h-1.73c-.422 0-.767.345-.767.767v10.452c0 .422.345.767.767.767h1.73c.422 0 .767-.345.767-.767V6.781c0-.422-.345-.767-.767-.767zm-5.623 0H3.77c-.422 0-.767.345-.767.767v10.452c0 .422.345.767.767.767h12.212c.422 0 .767-.345.767-.767V6.781c0-.422-.345-.767-.767-.767z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">
                      {t("downloads.androidApp")}
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                      {t("downloads.androidAppDesc")}
                    </p>
                    <p className="text-xs font-medium text-primary-700 dark:text-primary-300 mt-1">
                      {t("downloads.versionInfo")}
                    </p>
                  </div>
                </div>
                <a
                  href="https://drive.google.com/file/d/1LaFfy8B7NmeHYyWx_HBCerJaVlCp7cv-/view?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5 flex items-center space-x-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>{t("downloads.downloadNow")}</span>
                </a>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
              {t("downloads.requirements")}
            </h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {t("downloads.requirementsText")}
            </p>
          </div>

          {/* Upcoming Features Section */}
          <div className="glass-card p-8 rounded-2xl bg-gradient-to-br from-primary-50/50 to-accent-50/50 dark:from-primary-900/20 dark:to-accent-900/20 border-2 border-primary-200/50 dark:border-primary-800/50">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-secondary-500 to-accent-500 rounded-2xl shadow-glow mb-6">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>

              <h2 className="text-2xl font-bold bg-gradient-to-r from-secondary-600 to-accent-600 bg-clip-text text-transparent mb-4">
                🚀 Exciting Updates Coming Soon!
              </h2>

              <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-6 leading-relaxed">
                We're constantly working to make Streak Spark even better.
                Curious about what's next on our roadmap?
              </p>

              <div className="bg-white/60 dark:bg-neutral-800/60 rounded-xl p-6 mb-6 border border-neutral-200/50 dark:border-neutral-700/50">
                <p className="text-neutral-800 dark:text-neutral-200 font-medium mb-4">
                  🌟 Peek into our development pipeline and see the amazing
                  features we're crafting just for you!
                </p>

                <Link
                  to="/notes/n/upcoming-features-list-2dec57"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-secondary-500 to-accent-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>Discover Upcoming Features</span>
                </Link>
              </div>

              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                💡 Your feedback helps shape Streak Spark's future. Stay tuned
                for more!
              </p>
            </div>
          </div>
        </div>
        <SocialHandles />
        <Footer />
      </div>
    </>
  );
};

export default Downloads;
